import type { ValidationIssue } from '../api/types.js'
import { graphIssues } from './validation.js'
import type { GraphNode } from './graph.js'

export interface ValidationBadgesProps {
  issues: readonly ValidationIssue[]
  nodes: readonly GraphNode[]
  /** Отказ самой проверки: сеть или права, а не замечание к графу. */
  error?: string | null
  onSelectNode?: (nodeId: string) => void
}

function nodeNameOf(nodes: readonly GraphNode[], nodeId: string): string {
  return nodes.find((node) => node.id === nodeId)?.name ?? nodeId
}

/**
 * Список замечаний валидатора (FR-UI-001). Показываются все разом, а не по
 * одному за попытку публикации: иначе исправление сводится к циклу
 * «опубликовать — прочитать одно замечание — исправить».
 *
 * Замечание с узлом ведёт к узлу: искать на холсте по имени из текста —
 * работа, которую редактор обязан делать сам.
 */
export function ValidationBadges({
  issues,
  nodes,
  error = null,
  onSelectNode,
}: ValidationBadgesProps): React.JSX.Element {
  const общие = graphIssues(issues)
  const узловые = issues.filter((issue) => issue.nodeId !== null)

  return (
    <section className="замечания" aria-label="Замечания валидатора">
      {error !== null ? (
        // Молчаливый отказ проверки — худший исход: человек видит чистый
        // холст и думает, что замечаний нет.
        <p className="замечания__отказ" role="alert">
          {`Проверить граф не удалось: ${error}`}
        </p>
      ) : null}

      {issues.length === 0 && error === null ? (
        <p className="замечания__чисто">Замечаний нет: сценарий готов к публикации.</p>
      ) : null}

      {общие.length > 0 ? (
        <ul className="замечания__граф">
          {общие.map((issue) => (
            <li key={`${issue.code}:${issue.message}`}>{issue.message}</li>
          ))}
        </ul>
      ) : null}

      {узловые.length > 0 ? (
        <ul className="замечания__узлы">
          {узловые.map((issue) => (
            <li key={`${issue.nodeId ?? ''}:${issue.field ?? ''}:${issue.code}`}>
              <button
                type="button"
                onClick={() => {
                  onSelectNode?.(issue.nodeId as string)
                }}
              >
                {nodeNameOf(nodes, issue.nodeId as string)}
              </button>
              <span className="замечание__текст">{issue.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
